import { useState } from 'react';
import type { PatSummary } from '../api';
import { TopBar } from './TopBar';
import { GenerateMcpTokenButton } from './GenerateMcpTokenButton';
import { ManageMcpTokens } from './ManageMcpTokens';
import { McpSetupInstructions } from './McpSetupInstructions';

interface UserTokensPageProps {
  userEmail?: string | null;
}

/**
 * AOZONE-100: dedicated MCP token management page at /user/tokens. Relocates
 * the token affordances off the admin dashboard: the "Generate MCP token" button
 * above a table of the caller's active tokens (created / expiry / revoke).
 *
 * AOZONE-105: a successful mint hands the new token's `PatSummary` straight to the
 * table, which prepends it. It does NOT trigger a re-list — the list is served by an
 * eventually-consistent GSI Query, so a refetch can miss a just-minted token, and a
 * user who doesn't see theirs mints again and ends up holding two live credentials.
 *
 * AOZONE-102: available to any signed-in user — there is no role gate. The PAT
 * endpoints scope every mint/list/revoke to the caller's own session email
 * server-side, which is the authz boundary.
 *
 * AOZONE-104: below the table, the MCP setup instructions — rendered
 * unconditionally, so the `/mbr link` prerequisite and the per-category
 * `create_entry` field rules are readable without minting anything.
 */
export function UserTokensPage({ userEmail }: UserTokensPageProps) {
  const [newToken, setNewToken] = useState<PatSummary | null>(null);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <TopBar userEmail={userEmail} showTokensLink />
      <main className="mx-auto max-w-[1280px] px-6 py-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            MCP tokens
          </h1>
          <GenerateMcpTokenButton onMinted={setNewToken} />
        </div>
        <ManageMcpTokens newToken={newToken} />
        <McpSetupInstructions />
      </main>
    </div>
  );
}
